/**
 * Home's List — every category with its figures, in the user's order
 * (DESIGN_SYSTEM §4.1). Opened by the List chip or a drag up on the pools.
 *
 * The pools are the glance; this is the reading. It replaced the old drag-up
 * drawer, whose list shrank as the water rose — worst on the 28th of a heavy
 * month, exactly when it was wanted. A sheet is as tall on the 28th as on the
 * 1st.
 *
 * Each row is a full-bleed band whose tint runs as far as the category has
 * filled its mark: a meter drawn in the row's own ground, not a card (D-1).
 * Due repeating expenses are confirmed at its top.
 */

import { type Cents, absCents, formatMoney } from "@/domain/money";
import { Sheet } from "@/features/common/sheet";
import { cx } from "@/features/common/primitives";
import { PendingRecurringBanner } from "@/features/recurring/pending_banner";
import type { CategoryStanding } from "@/hooks/use_data";

const PERCENT = 100;
/** How strongly a row's band takes its category's colour — enough to read, never enough to fight the text. */
const BAND_TINT = "24%";

function bandStyle(standing: CategoryStanding): React.CSSProperties | undefined {
  if (standing.budget === null) return undefined;
  const reach = Math.min(1, standing.ratio) * PERCENT;
  const tint = `color-mix(in srgb, ${standing.category.color} ${BAND_TINT}, transparent)`;
  return { background: `linear-gradient(90deg, ${tint} ${String(reach)}%, transparent ${String(reach)}%)` };
}

export function ListSheet({
  title,
  standings,
  currency,
  onOpenCategory,
  onClose,
}: {
  title: string;
  standings: readonly CategoryStanding[];
  currency: string;
  onOpenCategory: (id: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const money = (value: Cents): string => formatMoney(value, currency);
  return (
    <Sheet title={title} onClose={onClose}>
      <PendingRecurringBanner />
      <ul aria-label="Categories" className="border-t border-line">
        {standings.map((standing) => {
          const over = standing.isOver && standing.remaining !== null;
          return (
            <li key={standing.category.id} className="border-b border-line">
              <button
                type="button"
                onClick={() => onOpenCategory(standing.category.id)}
                style={bandStyle(standing)}
                className="flex min-h-[44px] w-full flex-wrap items-center justify-between gap-x-phi-0 px-phi-1 py-phi-n2 text-left"
              >
                <span className="flex min-w-0 items-center gap-phi-n2">
                  <span
                    aria-hidden="true"
                    className="size-phi-n1 shrink-0 rounded-full"
                    style={{ backgroundColor: standing.category.color }}
                  />
                  <span className="min-w-0">
                    <span className="block text-phi-0">{standing.category.name}</span>
                    <span className="fig block text-phi-n1 text-tx-2">
                      {standing.budget === null
                        ? "No budget"
                        : `${money(standing.spent)} of ${money(standing.budget)}`}
                    </span>
                  </span>
                </span>
                <span className={cx("fig ml-auto text-phi-0 font-semibold", over ? "text-over" : "text-tx")}>
                  {standing.budget === null
                    ? `${money(standing.spent)} spent`
                    : over
                      ? `${money(absCents(standing.remaining))} over`
                      : `${money(standing.remaining ?? standing.spent)} left`}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Sheet>
  );
}
